 
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getEmployeeTask, getTaskFiles, getMyWorkEntries, createEmployeeReport, uploadReceipt, downloadDocument } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { WorkReportDialog } from '../../components/WorkReportDialog';
import { EmployeeTaskMap } from '../../components/map/ProjectMapSection';
import { ENTRY_STATUS, TASK_STATUS, formatAmount, formatDate, mapsUrl } from '../../lib/workStatus';
import { ArrowLeft, MapPin, Navigation, FileText, Download, Send, Receipt, ClipboardList, Info } from 'lucide-react';
import { toast } from 'sonner';

const InfoRow = ({ label, value, testId }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-gray-100 last:border-0 text-sm" data-testid={testId}>
    <span className="text-[#2E2E2E]/60">{label}</span>
    <span className="font-medium text-right text-[#2E2E2E]">{value || '-'}</span>
  </div>
);

const EMPTY_EXPENSE = { category: '', amount: '', note: '', receipt: null, receiptName: '' };

const ExpensesForm = ({ taskId }) => {
  const [text, setText] = useState('');
  const [expenses, setExpenses] = useState([{ ...EMPTY_EXPENSE }]);
  const [saving, setSaving] = useState(false);

  const change = (i, field, value) => setExpenses((prev) => prev.map((e, idx) => (idx === i ? { ...e, [field]: value } : e)));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = [];
      for (const expense of expenses) {
        if (!expense.category || !expense.amount) continue;
        let receiptId = null; let receiptName = null; let receiptSize = null;
        if (expense.receipt) {
          const uploaded = await uploadReceipt(expense.receipt);
          receiptId = uploaded.id; receiptName = uploaded.original_filename; receiptSize = uploaded.size;
        }
        payload.push({ category: expense.category, amount: Number(expense.amount), note: expense.note || '', receipt_id: receiptId, receipt_name: receiptName, receipt_size: receiptSize });
      }
      if (!payload.length) { toast.error('Pievienojiet vismaz vienu izdevumu'); return; }
      await createEmployeeReport(taskId, { content: text || 'Izdevumi', expenses: payload });
      toast.success('Izdevumi iesniegti');
      setText(''); setExpenses([{ ...EMPTY_EXPENSE }]);
    } catch (error) {
      toast.error('Neizdevās iesniegt izdevumus');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="employee-task-expenses">
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Piezīme par izdevumiem (neobligāti)" data-testid="employee-task-expense-text" />
      {expenses.map((expense, i) => (
        <div key={i} className="grid grid-cols-2 gap-2 rounded-xl border p-3" data-testid={`employee-task-expense-row-${i}`}>
          <Input placeholder="Kategorija (degviela, krāsa...)" value={expense.category} onChange={(e) => change(i, 'category', e.target.value)} data-testid={`employee-task-expense-category-${i}`} />
          <Input type="number" step="0.01" placeholder="Summa €" value={expense.amount} onChange={(e) => change(i, 'amount', e.target.value)} data-testid={`employee-task-expense-amount-${i}`} />
          <Input className="col-span-2" placeholder="Piezīme" value={expense.note} onChange={(e) => change(i, 'note', e.target.value)} data-testid={`employee-task-expense-note-${i}`} />
          <Input className="col-span-2" type="file" accept="image/*,application/pdf" onChange={(e) => change(i, 'receipt', e.target.files?.[0] || null)} data-testid={`employee-task-expense-receipt-${i}`} />
        </div>
      ))}
      <div className="flex gap-2">
        <Button variant="outline" onClick={() => setExpenses((p) => [...p, { ...EMPTY_EXPENSE }])} data-testid="employee-task-expense-add">+ Izdevums</Button>
        <Button onClick={submit} disabled={saving} className="flex-1 bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="employee-task-expense-submit">{saving ? 'Saglabā...' : 'Iesniegt izdevumus'}</Button>
      </div>
    </div>
  );
};

const EmployeeTaskDetail = () => {
  const { taskId } = useParams();
  const [task, setTask] = useState(null);
  const [files, setFiles] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reportOpen, setReportOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [taskData, fileData, entryData] = await Promise.all([getEmployeeTask(taskId), getTaskFiles(taskId).catch(() => []), getMyWorkEntries({ task_id: taskId }).catch(() => [])]);
      setTask(taskData); setFiles(fileData || []); setEntries(entryData || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Neizdevās ielādēt uzdevumu');
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex items-center justify-center h-64" data-testid="employee-task-detail-loading"><div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (error || !task) {
    return (
      <Card className="border-0 shadow-sm max-w-xl" data-testid="employee-task-detail-empty">
        <CardContent className="py-10 text-center space-y-3">
          <p className="text-[#2E2E2E]/70" data-testid="employee-task-detail-error">{error || 'Uzdevums nav atrasts'}</p>
          <Button asChild variant="outline"><Link to="/portal/employee/tasks">Uz uzdevumiem</Link></Button>
        </CardContent>
      </Card>
    );
  }

  const status = TASK_STATUS[task.status] || { label: task.status, className: 'bg-gray-100 text-gray-700' };
  const percent = task.planned_amount ? Math.min(100, Math.round((task.reported_amount / task.planned_amount) * 100)) : null;
  const navUrl = mapsUrl(task);

  return (
    <div className="space-y-4 max-w-2xl pb-28 lg:pb-6" data-testid="employee-task-detail-page">
      <Link to="/portal/employee/tasks" className="inline-flex items-center gap-1 text-sm text-[#1F3D2B]" data-testid="employee-task-detail-back"><ArrowLeft className="w-4 h-4" /> Visi uzdevumi</Link>

      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1F3D2B] leading-tight" data-testid="employee-task-detail-title">{task.title}</h1>
          <p className="text-[#2E2E2E]/70" data-testid="employee-task-detail-worktype">{task.work_type_label || 'Darbs'}{task.planned_amount ? ` · plānots ${formatAmount(task.planned_amount, task.unit)}` : ''}</p>
        </div>
        <Badge className={`${status.className} text-sm`} data-testid="employee-task-detail-status">{status.label}</Badge>
      </div>

      {percent !== null && (
        <Card className="border-0 shadow-sm" data-testid="employee-task-detail-progress">
          <CardContent className="p-4">
            <div className="flex justify-between text-sm mb-2"><span className="text-[#2E2E2E]/60">Paveikts</span><span className="font-semibold">{formatAmount(task.reported_amount, task.unit)} / {formatAmount(task.planned_amount, task.unit)} · {percent}%</span></div>
            <div className="h-3 w-full rounded-full bg-gray-200"><div className="h-3 rounded-full bg-gradient-to-r from-[#1F3D2B] to-green-600 transition-all" style={{ width: `${percent}%` }} /></div>
          </CardContent>
        </Card>
      )}

      <Card className="border-0 shadow-sm" data-testid="employee-task-detail-object">
        <CardContent className="p-4">
          <h2 className="font-semibold text-[#1F3D2B] flex items-center gap-2 mb-2"><MapPin className="w-4 h-4" /> Objekts</h2>
          <InfoRow label="Nosaukums" value={task.project_name || task.property_name} testId="employee-task-detail-project" />
          <InfoRow label="Kadastra Nr." value={task.cadastral_number} testId="employee-task-detail-cadastral" />
          <InfoRow label="Platība" value={task.area_ha ? formatAmount(task.area_ha, 'ha') : null} testId="employee-task-detail-area" />
          <InfoRow label="Atrašanās vieta" value={task.property_address} testId="employee-task-detail-property" />
          <InfoRow label="Termiņš" value={task.due_date ? formatDate(task.due_date) : null} testId="employee-task-detail-due" />
          {navUrl && (
            <Button asChild variant="outline" className="mt-3 w-full h-12" data-testid="employee-task-detail-navigate">
              <a href={navUrl} target="_blank" rel="noreferrer"><Navigation className="w-4 h-4 mr-2" /> Atvērt navigāciju</a>
            </Button>
          )}
        </CardContent>
      </Card>

      {task.project_id && <EmployeeTaskMap taskId={task.id} />}

      {(task.admin_notes || task.description) && (
        <Card className="border-0 shadow-sm bg-[#f8faf8]" data-testid="employee-task-detail-notes">
          <CardContent className="p-4">
            <h2 className="font-semibold text-[#1F3D2B] flex items-center gap-2 mb-2"><Info className="w-4 h-4" /> Administratora norādījumi</h2>
            {task.description && <p className="text-sm text-[#2E2E2E]/80 whitespace-pre-line" data-testid="employee-task-detail-desc">{task.description}</p>}
            {task.admin_notes && <p className="text-sm text-[#2E2E2E] whitespace-pre-line mt-2 font-medium" data-testid="employee-task-detail-admin-notes">{task.admin_notes}</p>}
          </CardContent>
        </Card>
      )}

      <Card className="border-0 shadow-sm" data-testid="employee-task-detail-files">
        <CardContent className="p-4">
          <h2 className="font-semibold text-[#1F3D2B] flex items-center gap-2 mb-2"><FileText className="w-4 h-4" /> Faili ({files.length})</h2>
          {files.length === 0 ? <p className="text-sm text-[#2E2E2E]/60">Nav pievienotu failu.</p> : (
            <div className="space-y-1">
              {files.map((file) => (
                <button key={file.id} type="button" onClick={() => downloadDocument(file.id, file.original_filename).catch(() => toast.error('Neizdevās lejupielādēt'))} className="w-full flex items-center justify-between rounded-lg px-3 py-2 hover:bg-[#f2f5f2] text-left text-sm" data-testid={`employee-task-file-${file.id}`}>
                  <span className="truncate">{file.original_filename}</span>
                  <Download className="w-4 h-4 text-[#1F3D2B] flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm" data-testid="employee-task-detail-entries">
        <CardContent className="p-4">
          <h2 className="font-semibold text-[#1F3D2B] flex items-center gap-2 mb-2"><ClipboardList className="w-4 h-4" /> Manas atskaites šim uzdevumam</h2>
          {entries.length === 0 ? <p className="text-sm text-[#2E2E2E]/60">Vēl nav atskaišu.</p> : entries.map((entry) => {
            const es = ENTRY_STATUS[entry.status] || { label: entry.status, className: '' };
            return (
              <div key={entry.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0 text-sm" data-testid={`employee-task-entry-${entry.id}`}>
                <span>{formatDate(entry.date)} · <strong>{formatAmount(entry.amount, entry.unit)}</strong>{entry.completed ? ' · pabeigts' : ''}</span>
                <Badge className={es.className}>{es.label}</Badge>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <details className="rounded-xl bg-white shadow-sm" data-testid="employee-task-detail-expenses-toggle">
        <summary className="cursor-pointer p-4 font-semibold text-[#1F3D2B] flex items-center gap-2"><Receipt className="w-4 h-4" /> Izdevumi un čeki</summary>
        <div className="px-4 pb-4"><ExpensesForm taskId={task.id} /></div>
      </details>

      {task.status !== 'completed' && (
        <div className="fixed bottom-0 left-0 right-0 lg:static lg:left-auto p-4 bg-white/95 backdrop-blur border-t lg:border-0 lg:bg-transparent lg:p-0 z-30 lg:ml-0" data-testid="employee-task-detail-cta">
          <Button onClick={() => setReportOpen(true)} className="w-full h-14 text-base bg-green-700 hover:bg-green-800 max-w-2xl mx-auto flex" data-testid="employee-task-detail-report-btn">
            <Send className="w-5 h-5 mr-2" /> Iesniegt paveikto darbu
          </Button>
        </div>
      )}

      <WorkReportDialog open={reportOpen} onOpenChange={setReportOpen} task={task} tasks={[task]} onSubmitted={load} />
    </div>
  );
};

export default EmployeeTaskDetail;
